import React, { useState, useEffect } from 'react'
import { appConfig, themeConfig } from 'config'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { Button, Col, Row, Space } from 'antd'
import {
  formatBirthdate,
  formatBusinessType,
  formatSex,
  enCryptParamMerchant,
  numberWithComma,
} from 'helpers/format'
import { Data, PointSummaryReq } from 'Store/merchants/types'
import { MerchantQrCode, MerchantStatusTag } from 'Components/utilities'
import { CrownOutlined, PhoneOutlined, ShopOutlined } from '@ant-design/icons'
import InfoItem from './InfoItem'
import { UploadForm } from 'Components/form'
import PointHistory from './PointHistory'
import { connect, useDispatch } from 'react-redux'
import { selectCurrentUser } from 'Store/user/selector'
import { getPointSummaryAPI } from 'Store/merchants/api'
import RootState from 'typings/RootState'
import { selectPointSummary } from 'Store/merchants/selector'

type Props = {
  mode: any
  formData?: any
  detail?: Data
  readonly?: boolean
  onUpdateData?: (file: string, key: string) => void
  token?: string
  showPointSummary?: boolean
} & ReduxProps

const MerchantInfo: React.FunctionComponent<Props> = props => {
  const {
    mode,
    detail,
    onUpdateData = () => {},
    token = '',
    formData,
    currentUser,
    pointSummary,
    showPointSummary = false,
  } = props
  const isApproveMode = mode === 'approve'
  const [drawer, setDrawer] = useState(false)
  const isRejected = detail?.rejected || false
  const dispatch = useDispatch()

  const merchantUrl = `${appConfig.publicFrontEndpoint}merchant-products/${enCryptParamMerchant(
    detail?.id || 0,
  )}`

  const onGetPointSummary = (): void => {
    const req: PointSummaryReq = {}
    dispatch(getPointSummaryAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  useEffect(() => {
    if (showPointSummary) {
      onGetPointSummary()
    }
  }, [])

  const renderInfo = (title: string, value: any, span: number = 15) => {
    return <InfoItem title={title} value={value} span={span} />
  }

  const PointHistoryDrawer = (
    <PointHistory id={detail?.id || 0} isShow={drawer} onClose={() => setDrawer(false)} />
  )

  return (
    <BoxWrapper>
      {PointHistoryDrawer}
      <Row justify="start" gutter={16}>
        <Col>
          <BoxTitle>ข้อมูลร้านค้า</BoxTitle>
        </Col>
        <Col>
          <MerchantStatusTag rejected={detail?.rejected} verified={detail?.verified || false} />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Row justify="center" align="middle" gutter={[16, 16]}>
            <Col>
              <AvartarWrapper>
                <Center>
                  <UploadForm
                    token={token}
                    onUpdateData={(file: any) => onUpdateData(file, 'logo')}
                    currentFile={formData?.logo || ''}
                    folder="merchant-logo"
                    listType="picture-card"
                    isPrivate={true}
                    name="logo"
                    description=""
                    isRequired={false}
                    isError={false}
                    label={''}
                    readOnly={isApproveMode}
                    withCropImage={true}
                    aspect={1}
                  />
                </Center>
                <Space direction="vertical" size="middle">
                  <Space direction="vertical" size="small">
                    <BoxTitle>{detail?.name}</BoxTitle>
                    <InfoTitle>ประเภท {formatBusinessType(detail?.type || '')}</InfoTitle>
                  </Space>
                  {detail?.verified && !detail?.rejected && showPointSummary && (
                    <Space direction="vertical" size="small">
                      <Button
                        type="primary"
                        size={'middle'}
                        icon={<CrownOutlined />}
                        onClick={() => setDrawer(true)}
                      >
                        คะแนนสะสมทั้งหมด {numberWithComma(pointSummary?.data?.point || 0)} คะแนน
                      </Button>
                      {(pointSummary?.data?.point_expire_next_day || 0) > 0 && (
                        <RedText>
                          คะแนนหมดอายุพรุ่งนี้{' '}
                          {numberWithComma(pointSummary?.data?.point_expire_next_day || 0)} คะแนน
                        </RedText>
                      )}
                    </Space>
                  )}
                  <Space>
                    {themeConfig?.component?.btnSeeMerchant && (
                      <a href={merchantUrl} target="_blank" rel="noopener noreferrer">
                        <Button type="default" size={'middle'} icon={<ShopOutlined />}>
                          ดูร้านค้า
                        </Button>
                      </a>
                    )}
                    <a href={`tel:${detail?.phone}`}>
                      <Button type="default" size={'middle'} icon={<PhoneOutlined />}>
                        โทรศัพท์
                      </Button>
                    </a>
                  </Space>
                </Space>
              </AvartarWrapper>
            </Col>
          </Row>
          <br />
          {/* {renderInfo('ชื่อร้านค้า', detail?.name)} */}
          {renderInfo('รหัสร้านค้า', detail?.id)}
          {/* {renderInfo('ประเภทร้านค้า', formatBusinessType(detail?.type || ''))} */}
          {renderInfo('ชื่อเจ้าของร้าน', `${detail?.owner_firstname || ''} ${detail?.owner_lastname || ''}`)}
          {renderInfo('เบอร์ติดต่อร้านค้า', detail?.phone)}
          {renderInfo('อีเมล', detail?.email)}
          {renderInfo('วันเกิด', formatBirthdate(detail?.owner_birthday || ''))}
          {renderInfo('เพศ', formatSex(detail?.owner_sex || ''))}
          {renderInfo('เบอร์ติดต่อเจ้าของร้าน', detail?.owner_phone || '')}
          {renderInfo('รหัสแนะนำร้านค้า', detail?.invite_code || '')}
          {isRejected &&
            renderInfo('เหตุผลที่ไม่อนุมัติ', <RejectReason>{detail?.reject_reason || ''}</RejectReason>)}
          {!isRejected &&
            !isApproveMode &&
            themeConfig?.component?.qrCodeMerchant &&
            renderInfo(
              'QR Code ร้านค้า',
              <MerchantQrCode title={detail?.name || ''} merchantID={detail?.id || 0} />,
            )}
        </Col>
      </Row>
    </BoxWrapper>
  )
}

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
  //color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

const BoxWrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  padding: 15px 10px;
  //border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
  border: 1px solid #f0f0f0;
  margin-bottom: 10px;
  background-color:  ${(props: { theme: MyTheme }): string => props.theme.colors.white};
`

const InfoTitle = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
`

const InfoValue = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const RedText = styled(InfoValue)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.error};
`

const RejectReason = styled(InfoValue)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.error};
`
const AvartarWrapper = styled.div`
  text-align: center;
  .ant-upload.ant-upload-select-picture-card,
  .ant-upload-list-picture .ant-upload-list-item,
  .ant-upload-list-picture-card .ant-upload-list-item,
  .ant-upload-list-picture-card .ant-upload-list-item-info {
    border-radius: 50%;
  }

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-btn-primary,
  .ant-btn {
    border-radius: 10px;
    box-shadow: none;
  }

  .ant-upload-list-picture-card .ant-upload-list-item-thumbnail,
  .ant-upload-list-picture-card .ant-upload-list-item-thumbnail img {
    object-fit: cover;
  }
`

const Center = styled.div`
  width: max-content;
  margin: 0 auto;
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    pointSummary: selectPointSummary(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(MerchantInfo)
